import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import LoadingButton from "@material-ui/lab/LoadingButton";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteIcon from "@material-ui/icons/Delete";
import LayersIcon from "@material-ui/icons/Layers";
import AssignmentIcon from "@material-ui/icons/Assignment";
import { useSnackbar } from "notistack";
import SelectMenu from "../SelectMenu";
import AdjuntarPoderCliente from "./AdjuntarPoderCliente";
import UnirPDF from "../UnirPDF";
import GenerarPoderDialog from "./GenerarPoderDialog";
import { ajustesApi } from "src/services/ajustesApi";
import {
  clientesApi,
  useLazyGenerarUrlPrefirmadaPoderQuery,
  useDeletePoderClienteMutation,
} from "src/services/clientesApi";
import { cardStyles, UI_COLORS } from "../cliente/styles/clientePortalStyles";
import { useSelector } from "react-redux";

const TIPO_CLIENTE_OPCIONES = ["EMPRESA", "PERSONA", "SOCIO"];
const ESTADO_OPCIONES = ["ACTIVO", "INACTIVO"];

const validationSchema = Yup.object().shape({
  correo: Yup.string()
    .email("El correo ingresado no es válido")
    .required("El correo es requerido"),
  correoFacturacion: Yup.string().email(
    "El correo de facturación no es válido",
  ),
  telefono: Yup.string()
    .matches(/^\d{9}$/, "El teléfono debe tener 9 dígitos")
    .nullable(),
});

// Estilos para campos de solo lectura
const readOnlySx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: UI_COLORS.background.default,
  },
};

// Encabezado visual de sección con label y divisor
const SectionHeader = ({ title }) => (
  <Box sx={{ mt: 3, mb: 2 }}>
    <Typography
      variant="caption"
      sx={{
        display: "block",
        fontWeight: 700,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        mb: 1,
      }}
    >
      {title}
    </Typography>
    <Divider />
  </Box>
);

export default function EditarCamposCliente({ value, cliente }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [generarUrlPrefirmadaPoder] = useLazyGenerarUrlPrefirmadaPoderQuery();
  const [deletePoderCliente] = useDeletePoderClienteMutation();

  const [adjuntar, setAdjuntar] = useState(false);
  const [unirPDF, setUnirPDF] = useState(false);
  const [generarPoder, setGenerarPoder] = useState(false);

  const {
    sucursal: { refSucursal },
  } = useSelector((state) => state.auth.user);

  // ─── RTK Query ───────────────────────────────────────────────────────────────
  const { data: comunas = [] } = ajustesApi.useGetComunasQuery();
  const { data: complejidades = [], isSuccess: complejidadesCargadas } =
    ajustesApi.useGetComplejidadesClienteQuery();
  const { data: gruposData = [] } =
    ajustesApi.useGetSelectorElementoQuery(1297451858);
  const { data: girosData = [] } =
    ajustesApi.useGetSelectorElementoQuery(1813328154);
  const { data: poderData, refetch: refetchPoder } =
    clientesApi.useGetPoderClienteQuery(
      { rut: cliente?.rut, sucursal: refSucursal },
      { skip: !cliente?.rut || !refSucursal },
    );
  const [editarCliente] = clientesApi.useEditarClienteMutation();
  // ─────────────────────────────────────────────────────────────────────────────

  const archivoObtenido = poderData?.[0] ?? null;

  const grupos = gruposData
    .map((g) => g.nombreElemento)
    .sort((a, b) => a.localeCompare(b));

  const giros = girosData.map((g) => g.nombreElemento);

  // ─── Formik ──────────────────────────────────────────────────────────────────
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      rut: cliente?.rut || "",
      nombre: cliente?.nombre || "",
      direccion: cliente?.direccion || "",
      telefono: cliente?.telefono || "",
      correo: cliente?.correo || "",
      correoFacturacion: cliente?.correoFacturacion || "",
      contacto: cliente?.contacto || "",
      fechaIngreso: cliente?.fechaIngreso || "",
      fechaTermino: cliente?.fechaTermino || "",
      comentarios: cliente?.comentarios || "",
      estado: cliente?.estado || "ACTIVO",
      refComplejidad: cliente?.refComplejidad ?? null,
      comuna: cliente?.comuna || "",
      tipoCliente: cliente?.tipoCliente || "",
      grupo: cliente?.grupo || "",
      gIRO: cliente?.gIRO || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const { rut, fechaIngreso, fechaTermino, ...editableFields } = values;
        const datos = Object.fromEntries(
          Object.entries(editableFields).map(([key, val]) => [
            key,
            key === "refComplejidad"
              ? val
              : val === null || val === undefined
                ? null
                : String(val).trim().toUpperCase(),
          ]),
        );
        await editarCliente({ rut: cliente.rut, datos }).unwrap();
        enqueueSnackbar("Datos del cliente guardados", { variant: "success" });
      } catch (error) {
        enqueueSnackbar(
          error?.data?.message || "Error al guardar los datos del cliente",
          { variant: "error" },
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setFieldValue,
  } = formik;
  // ─────────────────────────────────────────────────────────────────────────────

  const handleDeleteFile = async () => {
    setLoading(true);
    try {
      if (archivoObtenido) {
        await deletePoderCliente({
          id: archivoObtenido.id,
          sucursal: refSucursal,
        }).unwrap();
        refetchPoder();
      }
    } catch {
      enqueueSnackbar("Error al eliminar el poder", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const descargarPoder = async () => {
    if (!archivoObtenido) return;

    if (archivoObtenido.s3key) {
      setLoading(true);
      try {
        const result = await generarUrlPrefirmadaPoder({
          id: archivoObtenido.id,
          sucursal: refSucursal,
        }).unwrap();

        if (result && result.success) {
          window.open(result.data, "_blank");
        } else {
          enqueueSnackbar("Error al obtener el poder", { variant: "error" });
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Error al obtener el poder", { variant: "error" });
      } finally {
        setLoading(false);
      }
    } else {
      window.open(archivoObtenido.urlPoder, "_blank");
    }
  };

  if (value !== 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Card variant="outlined" sx={{ ...cardStyles, borderRadius: 0 }}>
        <CardContent sx={{ px: 3, pt: 3 }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{ textAlign: "center", fontWeight: 700, mb: 3 }}
          >
            Datos del Cliente
          </Typography>

          {/* ── Sección Poder Notarial ── */}
          <Box
            sx={{
              backgroundColor: UI_COLORS.background.filters,
              border: `1px solid ${UI_COLORS.borders.light}`,
              borderRadius: 2,
              p: 2.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontWeight: 700,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                mb: 1.5,
              }}
            >
              Poder Notarial
            </Typography>
            <Grid container spacing={1.5}>
              {archivoObtenido ? (
                <>
                  <Grid item xs={12} md={8}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      startIcon={<GetAppIcon />}
                      onClick={descargarPoder}
                    >
                      Descargar Poder
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <LoadingButton
                      fullWidth
                      loading={loading}
                      onClick={handleDeleteFile}
                      color="error"
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                    >
                      Eliminar
                    </LoadingButton>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => setAdjuntar(true)}
                    >
                      Subir Poder
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<LayersIcon />}
                      onClick={() => setUnirPDF(true)}
                    >
                      Unir PDF
                    </Button>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="info"
                  startIcon={<AssignmentIcon />}
                  onClick={() => setGenerarPoder(true)}
                >
                  Generar Poder
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* ── Sección Información General ── */}
          <SectionHeader title="Información General" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="RUT"
                disabled
                variant="outlined"
                sx={readOnlySx}
                {...getFieldProps("rut")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre"
                variant="outlined"
                {...getFieldProps("nombre")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: -2 }}>
                <SelectMenu
                  options={ESTADO_OPCIONES}
                  label="Estado"
                  value={values.estado}
                  onChange={(val) => setFieldValue("estado", val ?? "ACTIVO")}
                  disableClearable={true}
                  getOptionLabel={(opt) =>
                    opt === "ACTIVO" ? "Activo" : "Inactivo"
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: -2 }}>
                <SelectMenu
                  options={TIPO_CLIENTE_OPCIONES}
                  label="Tipo Cliente"
                  value={values.tipoCliente || null}
                  onChange={(val) => setFieldValue("tipoCliente", val ?? "")}
                  getOptionLabel={(opt) =>
                    opt.charAt(0) + opt.slice(1).toLowerCase()
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: -2 }}>
                <SelectMenu
                  options={complejidadesCargadas ? complejidades : []}
                  value={
                    complejidadesCargadas
                      ? (complejidades.find(
                          (c) => c.id === values.refComplejidad,
                        ) ?? null)
                      : null
                  }
                  onChange={(val) =>
                    setFieldValue("refComplejidad", val?.id ?? null)
                  }
                  label="Nivel de Complejidad"
                  disableClearable={true}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: -2 }}>
                <SelectMenu
                  options={grupos}
                  label="Grupo"
                  value={values.grupo || null}
                  onChange={(val) => setFieldValue("grupo", val ?? "")}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: -2 }}>
                <SelectMenu
                  options={giros}
                  label="GIRO"
                  value={values.gIRO || null}
                  onChange={(val) => setFieldValue("gIRO", val ?? "")}
                />
              </Box>
            </Grid>
          </Grid>

          {/* ── Sección Información de Contacto ── */}
          <SectionHeader title="Información de Contacto" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono"
                variant="outlined"
                {...getFieldProps("telefono")}
                error={Boolean(touched.telefono && errors.telefono)}
                helperText={touched.telefono && errors.telefono}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Correo Personal"
                variant="outlined"
                {...getFieldProps("correo")}
                error={Boolean(touched.correo && errors.correo)}
                helperText={touched.correo && errors.correo}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Correo Facturación"
                variant="outlined"
                {...getFieldProps("correoFacturacion")}
                error={Boolean(
                  touched.correoFacturacion && errors.correoFacturacion,
                )}
                helperText={
                  touched.correoFacturacion && errors.correoFacturacion
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contacto"
                variant="outlined"
                {...getFieldProps("contacto")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dirección"
                variant="outlined"
                {...getFieldProps("direccion")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: -2 }}>
                <SelectMenu
                  options={comunas.map((c) => c.nombre)}
                  label="Seleccione Comuna"
                  value={values.comuna || null}
                  onChange={(val) => setFieldValue("comuna", val ?? "")}
                />
              </Box>
            </Grid>
          </Grid>

          {/* ── Sección Notas y Fechas ── */}
          <SectionHeader title="Notas y Fechas" />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas sobre el cliente"
                variant="outlined"
                multiline
                rows={3}
                {...getFieldProps("comentarios")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha Ingreso"
                disabled
                variant="outlined"
                sx={readOnlySx}
                {...getFieldProps("fechaIngreso")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha Término"
                disabled
                variant="outlined"
                sx={readOnlySx}
                {...getFieldProps("fechaTermino")}
              />
            </Grid>
          </Grid>
        </CardContent>

        <CardActions sx={{ justifyContent: "center", pb: 3, px: 3 }}>
          <LoadingButton
            size="large"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
            sx={{ minWidth: 180 }}
          >
            Guardar Cambios
          </LoadingButton>
        </CardActions>
      </Card>

      {adjuntar && (
        <AdjuntarPoderCliente
          open={adjuntar}
          setOpen={setAdjuntar}
          setLoading={setLoading}
          cliente={cliente}
          setCliente={() => {}}
          archivoObtenido={archivoObtenido}
          setArchivoObtenido={() => refetchPoder()}
          setShowAlert={() => {}}
          setMessage={() => {}}
        />
      )}
      {unirPDF && (
        <UnirPDF cliente={cliente} open={unirPDF} setOpen={setUnirPDF} />
      )}
      {generarPoder && (
        <GenerarPoderDialog
          open={generarPoder}
          setOpen={setGenerarPoder}
          cliente={cliente}
        />
      )}
    </Box>
  );
}
